<script setup>
  import { watch } from 'vue';
  import InputField from '@/components/form/InputField.vue';
  import Spinner from '@/components/Spinner.vue';
  import InlineErrorMessage from '@/components/error/InlineErrorMessage.vue';
  import {
    validatePostalCode,
    validatePhoneNumber,
  } from '@/composables/useInputValidation.js';

  const emit = defineEmits(['createAccount', 'gotoStep']);

  const props = defineProps({
    steps: {
      type: Number,
      default: 0,
    },
    creating: {
      type: Boolean,
      default: false,
    },
    userAddress: {
      type: Object,
      default: () => {},
    },
    validationErrors: {
      type: Object,
      default: () => {},
    },
  });

  // Prevent invalid characters in postal code
  const handlePostalCodeKeydown = (event) => {
    const key = event.key;

    // Allow control keys
    if (
      key === 'Backspace' ||
      key === 'Delete' ||
      key === 'Tab' ||
      key === 'Escape' ||
      key === 'Enter' ||
      key === 'ArrowLeft' ||
      key === 'ArrowRight' ||
      key === 'ArrowUp' ||
      key === 'ArrowDown' ||
      key === 'Home' ||
      key === 'End' ||
      event.ctrlKey ||
      event.metaKey // Allow Ctrl/Cmd shortcuts
    ) {
      return;
    }

    // Only allow alphanumeric and space
    if (!/^[0-9A-Za-z\s]$/.test(key)) {
      event.preventDefault();
    }
  };

  // Format postal code on input
  const handlePostalCodeInput = (event) => {
    const input = event.target;
    const oldValue = input.value;

    // Format the postal code
    const result = validatePostalCode(oldValue);
    const newValue = result.formatted || oldValue;

    if (newValue !== oldValue) {
      props.userAddress.zip = newValue;
    }
  };

  // Prevent invalid characters in phone number
  const handlePhoneKeydown = (event) => {
    const key = event.key;

    // Allow control keys
    if (
      key === 'Backspace' ||
      key === 'Delete' ||
      key === 'Tab' ||
      key === 'Escape' ||
      key === 'Enter' ||
      key === 'ArrowLeft' ||
      key === 'ArrowRight' ||
      key === 'ArrowUp' ||
      key === 'ArrowDown' ||
      key === 'Home' ||
      key === 'End' ||
      event.ctrlKey ||
      event.metaKey // Allow Ctrl/Cmd shortcuts
    ) {
      return;
    }

    // Only allow digits, space, and + (only at start)
    if (!/^[\d\s+]$/.test(key)) {
      event.preventDefault();
    }
  };

  // Format phone number on input
  const handlePhoneInput = (event) => {
    const input = event.target;
    const oldValue = input.value;

    // Format the phone number
    const result = validatePhoneNumber(oldValue);
    const newValue = result.formatted || oldValue;

    if (newValue !== oldValue) {
      props.userAddress.phone = newValue;
    }
  };

  function submitForm() {
    emit('createAccount');
  }
</script>

<template>
  <div class="mb-4 flex items-center justify-between">
    <h1 class="text-lg lg:text-xl xl:text-2xl font-bold text-gray-900">
      {{ $t('auth.registration.address_step.my_address') }}
    </h1>
    <span class="text-xs lg:text-sm text-gray-400">(3/{{ steps }})</span>
  </div>

  <form autocomplete="off" @submit.prevent="submitForm">
    <div class="flex items-center space-x-2">
      <InputField
        v-model="userAddress.street"
        required
        label="profile.form.street"
        labelSrOnly
        :placeholder="$t('profile.form.street')"
        class="w-2/3"
        :inputClasses="`!bg-[#F4F6F9] !pl-10 !pr-2.5 !py-3 block w-full text-sm rounded-xl !border-gray-200 focus:!ring-1 focus:!ring-primary-500 focus:!border-primary-500`"
        icon="fa-regular fa-house"
      />

      <InputField
        v-model="userAddress.number"
        required
        label="profile.form.number"
        labelSrOnly
        :placeholder="$t('profile.form.number')"
        class="w-1/3"
        :inputClasses="`!bg-[#F4F6F9] !px-2.5 !py-3 block w-full text-sm rounded-xl !border-gray-200 focus:!ring-1 focus:!ring-primary-500 focus:!border-primary-500`"
      />
    </div>

    <InlineErrorMessage
      v-if="validationErrors?.userAddress?.street"
      :msg="validationErrors?.userAddress?.street"
    />

    <InlineErrorMessage
      v-if="validationErrors?.userAddress?.number"
      :msg="validationErrors?.userAddress?.number"
    />

    <div class="mt-3 flex items-center space-x-2">
      <InputField
        v-model="userAddress.zip"
        required
        label="profile.form.zip"
        labelSrOnly
        class="w-1/3"
        maxlength="7"
        placeholder="1000 / 1000AA"
        :inputClasses="`!bg-[#F4F6F9] !pl-10 !pr-2.5 !py-3 block w-full text-sm rounded-xl !border-gray-200 focus:!ring-1 focus:!ring-primary-500 focus:!border-primary-500`"
        icon="fa-regular fa-location-dot"
        @keydown="handlePostalCodeKeydown"
        @input="handlePostalCodeInput"
      />

      <InputField
        v-model="userAddress.city"
        required
        label="profile.form.city"
        labelSrOnly
        :placeholder="$t('profile.form.city')"
        class="w-2/3"
        :inputClasses="`!bg-[#F4F6F9] !pl-10 !pr-2.5 !py-3 block w-full text-sm rounded-xl !border-gray-200 focus:!ring-1 focus:!ring-primary-500 focus:!border-primary-500`"
        icon="fa-regular fa-city"
      />
    </div>

    <InlineErrorMessage
      v-if="validationErrors?.userAddress?.zip"
      :msg="validationErrors?.userAddress?.zip"
    />

    <InlineErrorMessage
      v-if="validationErrors?.userAddress?.city"
      :msg="validationErrors?.userAddress?.city"
    />

    <InputField
      v-model="userAddress.phone"
      required
      label="profile.form.phone"
      labelSrOnly
      class="mt-3"
      inputType="tel"
      placeholder="+32 XXX XXX XXX of 0XXX XXX XXX"
      :inputClasses="`!bg-[#F4F6F9] !pl-10 !pr-2.5 !py-3 block w-full text-sm rounded-xl !border-gray-200 focus:!ring-1 focus:!ring-primary-500 focus:!border-primary-500`"
      icon="fa-regular fa-phone"
      @keydown="handlePhoneKeydown"
      @input="handlePhoneInput"
    />

    <InlineErrorMessage
      v-if="validationErrors?.userAddress?.phone"
      :msg="validationErrors?.userAddress?.phone"
    />
    <div class="mt-5 flex items-center justify-between">
      <button class="btn-cancel" type="button" @click="emit('gotoStep', 2)">
        {{ $t('generic.act.back') }}
      </button>
      <button
        class="btn-primary !rounded-full !px-5 !py-3 text-sm flex items-center justify-center"
        :class="{ 'pointer-events-none opacity-50': creating }"
        type="submit"
        :disabled="creating"
      >
        <span>
          {{ $t('auth.registration.address_step.create_my_account') }}
        </span>
        <span v-if="creating">
          <Spinner class="size-5 text-white ml-2" />
        </span>
      </button>
    </div>
  </form>
</template>
